import type { Translation } from "@/i18n/locales/types";

export const people: Translation["people"] = {
  title: "People",
  description:
    "Who signs in to Punto.Firma with Google. Besides the domain, each person has to be invited.",
  tabs: {
    users: "Users",
    groups: "Groups",
  },
  user: "Person",
  group: "Group",
  you: "You",
  invite: {
    button: "Invite",
    title: "Invite someone",
    description:
      "They sign in with their Google account using this email. There is no magic link: they go from invited to active the first time they sign in.",
    email: "Email",
    emailPlaceholder: "name@{{domain}}",
    emailHint: "It must be from {{domains}}.",
    name: "Name (optional)",
    role: "Role",
    roleHints: {
      admin:
        "Manages certificates, policies and people, and sees all activity.",
      user: "Sees their certificates and activity, and uses them from the Mac app.",
    },
    groups: "Groups (optional)",
    submit: "Invite",
    success: "Invitation created for {{email}}",
    errors: {
      email: "Enter a valid email.",
    },
  },
  users: {
    search: "Search by email or name",
    filters: {
      status: "Status",
      role: "Role",
      group: "Group",
    },
    columns: {
      person: "Person",
      role: "Role",
      status: "Status",
      groups: "Groups",
      lastLogin: "Last sign-in",
    },
    empty: "Nobody else yet",
    emptyDescription: "Invite your team so they can use the certificates.",
    invited: "Invited {{when}}",
  },
  groups: {
    new: "New group",
    newTitle: "New group",
    newDescription:
      "A group lets a policy give access to several people at once.",
    name: "Name",
    namePlaceholder: "Tax",
    descriptionLabel: "Description (optional)",
    members: "People (optional)",
    create: "Create group",
    created: "Group “{{name}}” created",
    search: "Search groups",
    columns: {
      name: "Name",
      description: "Description",
      members: "People",
    },
    empty: "No groups yet",
    emptyDescription:
      "Create groups (“Tax”, “Payroll”…) to give access to several people at once.",
    errors: {
      name: "Give it a name.",
    },
  },
  userDetail: {
    notFound: "This person is not in the organization.",
    changeRole: "Change role",
    changeRoleTo: "Make {{role}}",
    roleChanged: "Now {{role}}",
    suspend: "Suspend",
    suspendTitle: "Suspend {{name}}?",
    suspendDescription:
      "Takes effect immediately: their web and Mac app sessions are closed and their next signature with any certificate is denied. Nothing is deleted and they can be reactivated.",
    suspendConfirm: "Suspend now",
    suspended: "{{name}} is suspended and can no longer sign.",
    suspendedNote:
      "Suspended {{when}}. Cannot sign in or sign with any certificate.",
    reactivate: "Reactivate",
    reactivated: "{{name}} can sign in again.",
    remove: "Remove from organization",
    removeTitle: "Remove {{name}} from the organization?",
    removeDescription:
      "They lose all access: they leave their groups and the policies that name them, and their sessions are closed. Their activity log is kept.",
    removeConfirm: "Remove",
    removed: "{{name}} is no longer in the organization",
    lastLogin: "Last sign-in: {{when}}",
    neverLoggedIn: "Has not signed in yet",
    sessionsOpen_one: "{{count}} open app session",
    sessionsOpen_other: "{{count}} open app sessions",
    tabs: {
      access: "Access",
      groups: "Groups ({{count}})",
      activity: "Activity",
      sessions: "Sessions",
    },
  },
  access: {
    intro: "Which certificates they can use and why.",
    columns: {
      certificate: "Certificate",
      where: "Where",
      by: "Through",
    },
    except: "except {{what}}",
    withExceptions: "with exceptions",
    byGroup: "group {{name}}",
    empty: "Cannot use any certificate",
    emptyDescription:
      "No policy gives them access, either directly or through a group.",
  },
  userGroups: {
    add: "Add to a group",
    choose: "Choose a group",
    added: "Added to group “{{name}}”",
    removed: "Removed from group “{{name}}”",
    empty: "Not in any group",
    emptyDescription:
      "Groups let policies give access to several people at once.",
  },
  userActivity: {
    signatures: "Latest signatures",
    navigation: "Latest browsing",
    seeAll: "See everything in Activity",
    empty: "No activity yet",
    columns: {
      when: "When",
      what: "What",
      result: "Result",
    },
  },
  sessions: {
    intro:
      "Macs where the Punto.Firma app has signed in with this account. Closing a session forces that Mac to sign in again.",
    columns: {
      device: "Mac",
      created: "Since",
      lastUsed: "Last used",
      expires: "Expires",
    },
    close: "Close",
    closeTitle: "Close the session on “{{device}}”?",
    closeDescription:
      "The app on that Mac can no longer sign until it signs in again.",
    closeConfirm: "Close session",
    closed: "Session closed",
    empty: "No open sessions",
    emptyDescription:
      "They have not signed in to the Mac app, or the sessions were closed.",
    unknownDevice: "Unnamed Mac",
  },
  groupDetail: {
    notFound: "This group does not exist.",
    edit: "Edit",
    editTitle: "Edit group",
    saved: "Group saved",
    delete: "Delete group",
    deleteTitle: "Delete the group “{{name}}”?",
    deleteDescription:
      "Policies that use it stop applying to its members. People are not deleted.",
    deleteConfirm: "Delete",
    deleted: "Group “{{name}}” deleted",
    members: "People",
    addMember: "Add person",
    choosePerson: "Choose a person",
    memberAdded: "{{name}} added to the group",
    memberRemoved: "{{name}} removed from the group",
    removeMember: "Remove {{name}} from the group",
    membersEmpty: "The group is empty",
    membersEmptyDescription:
      "Add people so they get the access of its policies.",
    policies: "Policies that use it",
    policiesEmpty: "No policy uses it yet.",
  },
};
